import { CommonModule } from '@angular/common';
import { Component, Signal, OnInit, WritableSignal, signal, input, inject, InputSignal, computed } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { RouterModule } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SignalsStoreService } from '../../signals-store.service';
import { CartService } from '../../cart.service';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../../order.service';
import { NumberRestrictionDirective } from '../../directives/number-restriction.directive';
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { UpdateOrderParams } from '../../types/order.type';
import { setUpBundleChangesLog } from '../../common/bundle.utils';
import { updateFirebaseOrderAndSubTrigger } from '../../common/order.utils';
import { finalize, tap } from 'rxjs';
import { ProductType } from '../../types/common.enums';
import { FirebaseCrudService } from '../../firebase-crud.service';
import { isProductCutOffLocked, PRODUCT_CUTOFF_LOCK_LABEL } from '../../common/product-cutoff.util';

@Component({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    NumberRestrictionDirective,
    NgbTooltip
  ],
  selector: 'app-product-cart-preview',
  templateUrl: './product-cart-preview.component.html',
  styleUrl: './product-cart-preview.component.scss',
})

export class ProductCartPreview implements OnInit {
  signalsStoreService = inject(SignalsStoreService)
  orderService = inject(OrderService)
  #firebaseCrudService = inject(FirebaseCrudService);

  isInputValid!: boolean;
  previousValue!: number;
  /** Keeps a double click from firing two DELETEs for the same line. */
  isRemoving: WritableSignal<boolean> = signal(false);
  selectedQuantity: WritableSignal<any> = signal(1);
  newQuantity: WritableSignal<any> = signal(1);

  product: any = input(null, {
    alias: 'product',
    transform: (product: any) => {
      this.previousValue = product.quantity;
      this.newQuantity.set(product.quantity)
      return product;
    }
  })

  productKey: any = input(null, {
    alias: 'productKey'
  })

  allowModification: InputSignal<boolean> = input(true);

  /**
   * Removing a line and editing its quantity are separate capabilities (DEV-10936):
   * lines already saved into the order are read-only but can still be taken out.
   */
  allowRemoval: InputSignal<boolean> = input(true);

  /**
   * DEV-11456: this product closed earlier than the market for this delivery, so the
   * line is frozen whatever the caller allows. Read from the line itself rather than
   * from a new input: every caller renders order lines, and none of them should have
   * to know about bread.
   *
   * Unlike the order page there is no timer here — the side cart is opened and closed,
   * not left sitting open for hours, and it reads the same order that page does.
   */
  isCutOffLocked: Signal<boolean> = computed(() => isProductCutOffLocked(this.product()));

  canModify: Signal<boolean> = computed(() => this.allowModification() && !this.isCutOffLocked());

  canRemove: Signal<boolean> = computed(() => this.allowRemoval() && !this.isCutOffLocked());

  cutOffLockLabel = PRODUCT_CUTOFF_LOCK_LABEL;

  isJustAddedProductNotification: InputSignal<boolean> = input(false, {
    alias: 'isJustAddedProductNotification'
  });

  totalPrice = computed(() => this.#setupProductTotalPrice());


  ngOnInit(): void {
    this.isInputValid = true;
  }

  private validateInput(inputValue: number): void {
    const regex = /^\d+$/;
    this.isInputValid = regex.test(inputValue.toString());
  }

  onKeyUp(event: any): void {
    const { value } = event?.target
    this.validateInput(+value);
    if (!this.isInputValid)
      return

    this.newQuantity.set(+value)
  }

  updateQuantity() {
    if (!this.newQuantity() || this.newQuantity() === this.previousValue) return;
    // DEV-11456: the field is disabled while the line is closed, but a blur can still
    // land after the order comes back locked. Put it back where the order has it.
    if (this.isCutOffLocked()) return this.newQuantity.set(this.previousValue);
    this.#updateProduct();
  }

  removeCartProduct() {
    const odooOrder = this.orderService.order();

    if (!odooOrder?.id || this.isRemoving() || this.isCutOffLocked())
      return;

    this.isRemoving.set(true);

    this.orderService
      .deleteProductOrder(+odooOrder.id, this.product().lineId)
      .pipe(finalize(() => {
        this.isRemoving.set(false);
        updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService);
      }))
      .subscribe()
  }

  handleMinus() {
    const current = this.newQuantity();
    if (current <= 1) return;
    this.newQuantity.update((current) => current - 1);
    this.updateQuantity();
  }

  handlePlus() {
    const purchaseLimit = this.product()?.purchaseLimit || 0;
    if (purchaseLimit && this.newQuantity() >= purchaseLimit) return;
    this.newQuantity.update((current) => current + 1);
    this.updateQuantity();
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/product-placeholder.webp';
  }

  #updateProduct() {
    const savedQuantity = this.previousValue;
    let updated = false;

    const copy = this.product();
    copy.quantity = this.newQuantity();
    copy.bundleQuantityChanged = true;

    const obj: UpdateOrderParams = {
      orderId: this.orderService.order()?.id,
      product: { ...copy, variantId: copy.variant.id, packageId: copy.package?.id || null } as any,
      type: this.productKey(),
      bundleChangesLog: this.product()?.isBundle ? setUpBundleChangesLog(this.product(), this.newQuantity()) : undefined
    };

    this.orderService
      .updateOrder(obj, this.productKey() === ProductType.subscription)
      .pipe(
        tap(() => {
          updated = true;
          updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService);
        }),
        finalize(() => {
          if (!updated) {
            this.newQuantity.set(savedQuantity);
          }
        })
      )
      .subscribe();
  }

  #setupProductTotalPrice() {
    const p = this.product();
    if (!p.isBundle) return p.totalPrice;

    const totalPrice = this.orderService.calculateProductTotalPrice(p);
    return totalPrice;
  }
}
